import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import {
  Check,
  ArrowRight,
  Shield,
  Lock,
  Star,
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
} from "lucide-react";

const quotes = [
  {
    body: "We didn\u2019t lose them in the exit interview. We lost them six months earlier \u2014 we just weren\u2019t listening.",
    attribution: "Head of People \u00b7 140-person SaaS",
  },
  {
    body: "Our annual survey said 82% engaged. The first week of honest feedback said otherwise.",
    attribution: "VP People \u00b7 Series B fintech",
  },
  {
    body: "I finally got a paper trail on the manager everyone whispered about \u2014 before it became a lawsuit.",
    attribution: "HR Director \u00b7 Healthcare, 180 staff",
  },
  {
    body: "Three resignations prevented in the first quarter. The tool paid for itself in a week.",
    attribution: "Chief People Officer \u00b7 Agency, 90 people",
  },
];

export const EditorialHero = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (v.paused || v.ended) {
        await v.play();
      } else {
        v.pause();
      }
    } catch (e) {
      // Autoplay rejection or other; force a fresh play attempt
      v.muted = true;
      setIsMuted(true);
      await v.play().catch(() => undefined);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => undefined);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    v.currentTime = (Number(e.target.value) / 100) * duration;
  };

  const formatTime = (s: number) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <header className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-24 sm:pt-32 md:pt-40 pb-12 sm:pb-14 md:pb-32">
      {/* Soft sun glow ambient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-20 h-[520px] atrium-sun-glow opacity-70"
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-start">
        {/* Main column */}
        <div className="lg:col-span-7">

          {/* Headline */}
          <h1 className="text-[32px] xs:text-[38px] sm:text-6xl md:text-[88px] lg:text-[104px] leading-[1.05] sm:leading-[0.96] md:leading-[0.94] tracking-[-0.025em] text-foreground mb-4 sm:mb-6 md:mb-8 font-medium">
            What your team{" "}
            <span className="font-semibold text-primary">won&rsquo;t say</span>{" "}
            <span className="italic font-light text-foreground/85">in the survey.</span>
          </h1>

          {/* Subhead */}
          <p className="text-[15px] sm:text-xl md:text-[22px] text-foreground/65 leading-[1.55] max-w-[56ch] mb-6 sm:mb-8 md:mb-10 font-normal">
            The honest channel for People teams who are done with sanitized
            answers and exit-interview surprises. Truly anonymous &mdash; by
            architecture, not policy.
          </p>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 items-stretch sm:items-center">
            <button
              onClick={() => navigate("/auth?mode=signup")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 sm:px-8 py-3.5 sm:py-[18px] text-sm font-medium shadow-[0_8px_28px_rgba(62,92,77,0.28)] hover:shadow-[0_12px_36px_rgba(62,92,77,0.36)] hover:-translate-y-0.5 transition-all duration-300 min-h-[52px] sm:min-h-[56px] w-full sm:w-auto"
              aria-label="Start your free 15-day trial"
            >
              <span>Start listening honestly</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                const target = document.getElementById("how-it-works");
                target?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center rounded-full border border-foreground/15 bg-background/60 backdrop-blur-sm text-foreground px-6 sm:px-8 py-3.5 sm:py-[18px] text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300 min-h-[52px] sm:min-h-[56px] w-full sm:w-auto"
            >
              See how it works
            </button>
          </div>

          {/* Trial meta — soft chips */}
          <div className="mt-5 sm:mt-7 md:mt-8 flex flex-wrap items-center gap-1.5 sm:gap-2.5">
            {["15-day free trial", "No credit card", "Live in 2 minutes", "Cancel anytime"].map(
              (item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary/80 text-foreground/75 px-3 sm:px-3.5 py-1.5 text-[11px] sm:text-[12px] font-medium"
                >
                  <Check className="h-3 w-3 text-primary flex-shrink-0" strokeWidth={2.5} />
                  {item}
                </span>
              ),
            )}
          </div>
        </div>

        {/* Aside — Branded explainer video + trust stack */}
        <aside className="lg:col-span-5 lg:pl-2 flex flex-col gap-4">
          <figure className="atrium-card rounded-3xl p-3 md:p-4 relative overflow-hidden">
            <div className="flex items-center justify-between mb-3 px-3 pt-1">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-primary">
                Watch · 52 sec
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/60">
                <span className="inline-flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                Live
              </span>
            </div>
            <div className="relative group">
              <video
                ref={videoRef}
                className="w-full aspect-video rounded-2xl bg-secondary object-cover cursor-pointer"
                src="/sendfeedback-explainer.mp4"
                poster="/sendfeedback-explainer-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="SendFeedback.ai explainer video"
                onClick={togglePlay}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onDurationChange={(e) => setDuration(e.currentTarget.duration)}
              />

              {/* Big center play overlay — only when paused */}
              {!isPlaying && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlay();
                  }}
                  aria-label="Play video"
                  className="absolute inset-0 flex items-center justify-center rounded-2xl bg-foreground/20"
                >
                  <span className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-background/95 text-primary shadow-[0_8px_28px_rgba(21,34,27,0.35)] hover:scale-110 transition-transform duration-300">
                    <Play
                      className="h-5 w-5 sm:h-6 sm:w-6 ml-0.5"
                      fill="currentColor"
                      strokeWidth={0}
                    />
                  </span>
                </button>
              )}

              {/* Bottom control bar */}
              <div
                className="absolute inset-x-0 bottom-0 px-3 sm:px-4 pb-3 pt-8 rounded-b-2xl pointer-events-none opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200"
                style={{
                  background:
                    "linear-gradient(to top, rgba(21,34,27,0.75) 0%, rgba(21,34,27,0.35) 50%, rgba(21,34,27,0) 100%)",
                }}
              >
                <div className="flex items-center gap-2 sm:gap-3 pointer-events-auto">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                    aria-label={isPlaying ? "Pause" : "Play"}
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-background/95 text-primary hover:scale-110 transition-transform duration-200"
                  >
                    {isPlaying ? (
                      <Pause
                        className="h-3.5 w-3.5"
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    ) : (
                      <Play
                        className="h-3.5 w-3.5 ml-0.5"
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      restart();
                    }}
                    aria-label="Restart"
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-background/20 text-background hover:bg-background/30 transition-colors duration-200"
                  >
                    <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>

                  <span className="text-[11px] font-medium text-background/90 tabular-nums flex-shrink-0">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>

                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={0.1}
                    value={progress}
                    onChange={handleSeek}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Seek video"
                    className="flex-1 h-1 rounded-full appearance-none cursor-pointer accent-primary"
                    style={{
                      background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${progress}%, rgba(255,255,255,0.3) ${progress}%, rgba(255,255,255,0.3) 100%)`,
                    }}
                  />

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMute();
                    }}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-background/20 text-background hover:bg-background/30 transition-colors duration-200"
                  >
                    {isMuted ? (
                      <VolumeX className="h-3.5 w-3.5" strokeWidth={2.5} />
                    ) : (
                      <Volume2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <figcaption className="mt-3 px-3 pb-1 text-[12px] text-foreground/55 leading-snug">
              How honest, anonymous feedback turns into action — in under a minute.
            </figcaption>
          </figure>

          {/* Trust strip — fills the gap with social proof */}
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
            <div className="atrium-card rounded-2xl px-4 py-3.5 flex items-center gap-3">
              <div className="flex -space-x-2 flex-shrink-0">
                {["bg-primary/80", "bg-primary/60", "bg-primary/40"].map((c, i) => (
                  <span
                    key={i}
                    className={`h-7 w-7 rounded-full border-2 border-background ${c}`}
                  />
                ))}
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-[13px] font-medium text-foreground">
                  600+ teams
                </div>
                <div className="text-[11px] text-foreground/55">
                  listening this week
                </div>
              </div>
            </div>
            <div className="atrium-card rounded-2xl px-4 py-3.5 flex items-center gap-3">
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className="h-3.5 w-3.5 fill-primary text-primary"
                  />
                ))}
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-[13px] font-medium text-foreground">
                  4.9 / 5
                </div>
                <div className="text-[11px] text-foreground/55 whitespace-nowrap">
                  from 240+ HR leaders
                </div>
              </div>
            </div>
          </div>

          {/* Mini testimonial */}
          <figure className="atrium-card rounded-2xl px-4 sm:px-5 py-4">
            <blockquote className="text-[13px] leading-snug text-foreground/80 italic">
              &ldquo;We caught three resignations before they happened. The tool
              paid for itself in a week.&rdquo;
            </blockquote>
            <figcaption className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="text-[11px] text-foreground/55">
                Chief People Officer · 90-person agency
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-primary">
                <Shield className="h-3 w-3" strokeWidth={2.5} />
                Verified
              </span>
            </figcaption>
          </figure>

          {/* Security badges */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-1">
            <div className="flex items-center gap-1.5 text-[11px] text-foreground/55">
              <Lock className="h-3 w-3" strokeWidth={2.5} />
              <span>GDPR · SOC 2 ready</span>
            </div>
            <div className="text-[11px] text-foreground/55">
              EU-hosted · Zero metadata
            </div>
          </div>
        </aside>
      </div>

      {/* Proof bar — soft figure pills */}
      <div className="relative mt-12 sm:mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 md:gap-4">
        {[
          { figure: "85%", label: "Avg. response rate on honest channels" },
          { figure: "2 min", label: "From signup to first published page" },
          { figure: "0", label: "IPs, logins, or metadata captured" },
          { figure: "24/7", label: "AI HR Advisor for sensitive cases" },
        ].map((item) => (
          <div
            key={item.label}
            className="atrium-card rounded-2xl px-4 py-4 sm:px-5 sm:py-5 md:px-6 md:py-6"
          >
            <div className="text-[26px] sm:text-3xl md:text-[40px] font-medium tracking-tight text-foreground leading-none mb-2">
              {item.figure}
            </div>
            <div className="flex items-start gap-2">
              <Check
                className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
              <span className="text-[11px] sm:text-[12px] md:text-[13px] text-foreground/60 leading-snug">
                {item.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </header>
  );
};
